import { FileText } from "lucide-react";
import React from "react";
import type { Agreement } from "../../services/agreementsService";

interface UserAgreementsCardProps {
  appearance?: "default" | "alpha";
  isDarkMode: boolean;
  sidebarAgreements: Agreement[];
  sidebarAgreementsLoading: boolean;
}

const UserAgreementsCard: React.FC<UserAgreementsCardProps> = ({
  appearance = "default",
  isDarkMode,
  sidebarAgreements,
  sidebarAgreementsLoading,
}) => {
  return (
    /* Agreements Widget */
    <div className={appearance === "alpha"? `rounded-v7-panel border border-v7-ink/15 bg-v7-white` : `rounded-lg border ${isDarkMode ?"border-gray-700 bg-gray-800/30" : "border-gray-200 bg-white"}`}>
      <div className={appearance === "alpha" ? "flex items-center justify-between p-3 border-b border-v7-ink/15" : "flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700"}>
        <div className="flex items-center gap-2">
          <FileText className={appearance === "alpha"? `w-4 h-4 text-v7-ink/60` : `w-4 h-4 ${isDarkMode ?"text-gray-400" : "text-gray-500"}`} />
          <h4 className={appearance === "alpha"? `text-sm font-medium text-v7-ink-soft` : `text-sm font-medium ${isDarkMode ?"text-gray-200" : "text-gray-700"}`}>
            Agreements
          </h4>
          {!sidebarAgreementsLoading && sidebarAgreements.length > 0 && (
            <span className={appearance === "alpha" ? `px-1.5 py-0.5 text-xs rounded-full bg-v7-paper text-v7-bronze-dark` : `px-1.5 py-0.5 text-xs rounded-full ${
              isDarkMode ? "bg-teal-900/30 text-teal-300" : "bg-teal-100 text-teal-700"
            }`}>
              {sidebarAgreements.length}
            </span>
          )}
        </div>
      </div>
      <div className="p-3">
        {sidebarAgreementsLoading ? (
          <div className="flex items-center gap-2">
            <div className={appearance === "alpha" ? "animate-spin rounded-full h-4 w-4 border-b-2 border-v7-ink/25" : "animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500"}></div>
            <span className={appearance === "alpha"? `text-sm text-v7-ink/60` : `text-sm ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>Loading...</span>
          </div>
        ) : sidebarAgreements.length > 0 ? (
          <div className="space-y-2">
            {sidebarAgreements.map((agreement) => (
              <div
                key={agreement.id}
                className={appearance === "alpha" ? `p-2 rounded-v7-control bg-v7-paper` : `p-2 rounded ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className={appearance === "alpha"? `text-sm font-medium text-v7-ink-soft` : `text-sm font-medium ${isDarkMode ?"text-gray-200" : "text-gray-700"}`}>
                    {agreement.agreementName}
                  </span>
                  <span className={appearance === "alpha" ? `text-xs px-1.5 py-0.5 rounded-full bg-v7-paper text-v7-bronze-dark` : `text-xs px-1.5 py-0.5 rounded-full ${
                    isDarkMode ? "bg-teal-900/30 text-teal-400" : "bg-teal-100 text-teal-700"
                  }`}>
                    Signed
                  </span>
                </div>
                <div className={appearance === "alpha"? `text-xs mt-1 text-v7-ink/60` : `text-xs mt-1 ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                  {new Date(agreement.createdAtUtc).toLocaleString()}
                </div>
                {agreement.agreementDocument && (
                  <div className={appearance === "alpha"? `text-xs mt-1 truncate text-v7-ink/60` : `text-xs mt-1 truncate ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
                    {agreement.agreementDocument}
                  </div>
                )}
                {agreement.data && typeof (agreement.data as Record<string, unknown>).signedName === "string" && (
                  <div className={appearance === "alpha"? `text-xs mt-1 text-v7-ink/60` : `text-xs mt-1 ${isDarkMode ?"text-gray-400" : "text-gray-500"}`}>
                    Signed as: <span className="font-medium">{(agreement.data as Record<string, string>).signedName}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className={appearance === "alpha"? `text-sm italic text-v7-ink/60` : `text-sm italic ${isDarkMode ?"text-gray-500" : "text-gray-400"}`}>
            No agreements signed
          </div>
        )}
      </div>
    </div>
  );
};

export default UserAgreementsCard;
